import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { DunningAutoSendSetting, DunningAutoSendSettingResponse } from "../types"

const AUTO_SEND_SETTING_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_auto_send_setting`

const errorMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback

/** 部分更新のため、変更したキーだけを送る */
export type AutoSendSettingPatch = Partial<{
  enabled: boolean
  minOverdueDays: number
  maxOverdueDays: number
  minDaysSinceLastContact: number
  perRunCaseLimit: number
}>

/**
 * AIドラフト自動送付設定の読み書き:
 * - GET   … 現在の設定（サーバ側で1行運用。未作成でもデフォルト値が返る）
 * - PATCH … camelCase キーの部分更新（含めたキーだけが更新される）
 * 422（レンジ矛盾などのバリデーション）はサーバのメッセージをそのまま表示する。
 */
export const useDunningAutoSendSetting = () => {
  const { getWithToken, patchWithToken } = useSmoothApi()

  const settingSwr = useSWR(
    AUTO_SEND_SETTING_URL,
    async (fetchUrl: string) => {
      const response = await getWithToken(fetchUrl)
      return response.data as DunningAutoSendSettingResponse
    },
    { revalidateOnFocus: false }
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateSetting = async (
    patch: AutoSendSettingPatch
  ): Promise<DunningAutoSendSetting | undefined> => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const response = await patchWithToken(AUTO_SEND_SETTING_URL, patch)
      const body = response.data as DunningAutoSendSettingResponse
      // PATCH が最新の設定を返すため、再取得せずキャッシュを置き換える
      await settingSwr.mutate(body, { revalidate: false })
      return body.data
    } catch (e) {
      setSubmitError(errorMessage(e, "自動送付設定の更新に失敗しました。"))
      return undefined
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    setting: settingSwr.data?.data,
    isLoading: !settingSwr.data && !settingSwr.error,
    error: settingSwr.error,
    updateSetting,
    isSubmitting,
    submitError,
    clearSubmitError: () => setSubmitError(null),
  }
}
